import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { CommunityStats } from "../../shared/interfaces/GameLogicTypes";

interface StatItemProps {
  label: string;
  value: number;
  visible: boolean;
}

function roundToHundreds(n: number): string {
  if (n < 100) return String(n);
  return (Math.round(n / 100) * 100).toLocaleString();
}

function AnimatedCounter({ label, value, visible }: StatItemProps) {
  const [display, setDisplay] = useState(0);
  const rounded = roundToHundreds(value);

  useEffect(() => {
    if (!visible || value === 0) return;
    const duration = 1200;
    const start = performance.now();
    let raf: number;
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setDisplay(Math.round(eased * value));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [visible, value]);

  return (
    <Box sx={{ textAlign: "center", minWidth: { xs: "40%", sm: "auto" } }}>
      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{ color: "#ec7777", fontSize: { xs: "1.5rem", sm: "2rem" } }}
      >
        {roundToHundreds(display)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
        {label}
      </Typography>
    </Box>
  );
}

interface Props {
  stats: CommunityStats | null;
}

export default function CommunityStatsBar({ stats }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  if (!stats) return null;

  const items = [
    { label: "Card Decks", value: stats.deckCount },
    { label: "Questions", value: stats.questionCount },
    { label: "Answers", value: stats.answerCount },
    { label: "Categories", value: stats.categoryCount },
    { label: "Creators", value: stats.creatorCount },
    { label: "Drawings", value: stats.drawingCount },
  ];

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: { xs: 2, sm: 4 },
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        backgroundColor: "rgba(255,255,255,0.8)",
        backdropFilter: "blur(8px)",
      }}
    >
      {items.map((item) => (
        <AnimatedCounter key={item.label} {...item} visible={visible} />
      ))}
    </Box>
  );
}
